import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Video, AlertCircle, Send, Globe, CheckCircle2, Facebook, Instagram, Loader2 } from 'lucide-react';
import { LoginButton } from '@telegram-auth/react';

const API_URL = process.env.REACT_APP_API_URL;

const UnlockLink = () => {
  const { id } = useParams();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [pendingSteps, setPendingSteps] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [unlockedUrl, setUnlockedUrl] = useState('');
  const [telegramData, setTelegramData] = useState(null);
  const [failedSteps, setFailedSteps] = useState({});
  const activeStepRef = useRef(null);
  const stepStartTimeRef = useRef(null);
  const autoCompleteTimeoutRef = useRef(null);

  React.useEffect(() => {
    if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
      setTelegramData({
        id: window.Telegram.WebApp.initDataUnsafe.user.id,
        initData: window.Telegram.WebApp.initData
      });
    } else {
        
        const storedTgAuth = localStorage.getItem('telegramAuthData');
        if (storedTgAuth) {
            try {
                setTelegramData(JSON.parse(storedTgAuth));
            } catch(e) {}
        }
    }
  }, []);

  React.useEffect(() => {
    const fetchLinkData = async () => {
      try {
        const response = await axios.get(`${API_URL}/links/${id}`);
        setLinkData(response.data);
      } catch (err) {
        console.error("fetch error", err);
        setError('Link not found or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchLinkData();
  }, [id]);

  useEffect(() => {
    if (telegramData && telegramData.id && linkData) {
        const fetchProgress = async () => {
            try {
                const response = await axios.get(`${API_URL}/links/${id}/progress/${telegramData.id}`);
                if (response.data.completedSteps && response.data.completedSteps.length > 0) {
                    setCompletedSteps(response.data.completedSteps);
                }
            } catch (e) {
                console.error("Failed to fetch progress", e);
            }
        };
        fetchProgress();
    }
  }, [telegramData, id, linkData]);


  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && completedSteps.length === linkData?.steps?.length && !unlockedUrl && !verifying) {
         // Auto unlock if they return and everything is marked completed
         handleUnlock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [completedSteps, linkData, unlockedUrl, verifying]);


  useEffect(() => {
    if (telegramData && telegramData.id && completedSteps.length > 0) {
        axios.post(`${API_URL}/links/${id}/progress`, {
            telegramId: telegramData.id,
            completedSteps
        }).catch(e => console.error("Failed to save progress", e));
    }
  }, [completedSteps, telegramData, id]);

  useEffect(() => {
    const handleFocus = () => {
      if (activeStepRef.current !== null) {
        const elapsed = Date.now() - stepStartTimeRef.current;
        const index = activeStepRef.current;
        
        if (elapsed < 10000) {
          
          setPendingSteps(prev => prev.filter(i => i !== index));
          setFailedSteps(prev => ({ ...prev, [index]: 'You must complete the step and wait 10 seconds before returning.' }));
          clearTimeout(autoCompleteTimeoutRef.current);
          activeStepRef.current = null;
        } else {
          
          setPendingSteps(prev => prev.filter(i => i !== index));
          setCompletedSteps(prev => [...prev, index]);
          setFailedSteps(prev => {
              const newFailed = { ...prev };
              delete newFailed[index];
              return newFailed;
          });
          clearTimeout(autoCompleteTimeoutRef.current);
          activeStepRef.current = null;
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleStepClick = (index, url) => {
    window.open(url, '_blank');

    axios.post(`${API_URL}/links/${id}/click`).catch(() => {});

    if (!completedSteps.includes(index) && !pendingSteps.includes(index)) {
      setPendingSteps(prev => [...prev, index]);
      setFailedSteps(prev => {
          const newFailed = { ...prev };
          delete newFailed[index];
          return newFailed;
      });
      
      activeStepRef.current = index;
      stepStartTimeRef.current = Date.now();

      if (autoCompleteTimeoutRef.current) clearTimeout(autoCompleteTimeoutRef.current);
      autoCompleteTimeoutRef.current = setTimeout(() => {
          if (activeStepRef.current === index) {
              setPendingSteps(prev => prev.filter(i => i !== index));
              setCompletedSteps(prev => [...prev, index]);
              activeStepRef.current = null;
          }
      }, 11000);
    }
  };

  const [generalError, setGeneralError] = useState('');

  const handleUnlock = async () => {
    setVerifying(true);
    setGeneralError('');
    try {
      const response = await axios.post(`${API_URL}/links/${id}/verify`, {
        completedSteps,
        telegramData
      });
      setUnlockedUrl(response.data.destinationUrl);
      window.location.href = response.data.destinationUrl;
    } catch (err) {
      setGeneralError(err.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  if (!linkData) return null;

    const hasTelegramStep = linkData.steps.some(step => step.type === 'telegram');
  const needsTelegramAuth = hasTelegramStep && !telegramData;
  const allStepsCompleted = completedSteps.length === linkData.steps.length;

  const getStepIcon = (type) => {
    switch(type) {
      case 'youtube': return <Video className="h-6 w-6 text-red-600" />;
      case 'telegram': return <Send className="h-6 w-6 text-blue-500" />;
      case 'facebook': return <Facebook className="h-6 w-6 text-blue-600" />;
      case 'instagram': return <Instagram className="h-6 w-6 text-pink-500" />;
      default: return <Globe className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStepText = (step) => {
    switch(step.type) {
      case 'youtube': return 'Subscribe to YouTube Channel';
      case 'telegram': 
        if (step.chatName) return `Join ${step.chatName}`;
        return 'Join Telegram Channel';
      case 'facebook': return 'Follow on Facebook';
      case 'instagram': return 'Follow on Instagram';
      default: return 'Visit Website';
    }
  };

  const theme = linkData?.theme || {
      bgColor: '#06090e', 
      textColor: '#ffffff', 
      buttonColor: '#953bf6', 
      animation: 'none'
  };

  const getAnimationClass = () => {
    if (theme.animation === 'pulse') return 'animate-pulse';
    if (theme.animation === 'bounce') return 'animate-bounce';
    return '';
  };

  return (
    <div 
        className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 bg-cover bg-center bg-no-repeat relative" 
        style={{ 
            backgroundColor: theme.bgColor, 
            color: theme.textColor,
            backgroundImage: theme.bgImage ? `url(${theme.bgImage})` : 'none'
        }}
    >
      {theme.bgImage && <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-sm" />}
      <div className="max-w-md w-full bg-black/40 rounded-2xl shadow-2xl p-8 border border-white/20 backdrop-blur-md relative z-10">
        <h2 className="text-2xl font-bold text-center mb-2" style={{ color: theme.textColor }}>{linkData.title}</h2>
        <p className="text-center opacity-70 mb-8" style={{ color: theme.textColor }}>Complete the steps below to unlock your link</p>

        <div className="space-y-4 mb-8">
          {linkData.steps.map((step, index) => {
            const isCompleted = completedSteps.includes(index);
            return (
              <div key={index} className="flex flex-col">
              <button
                onClick={() => handleStepClick(index, step.url)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border ${
                  isCompleted
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : `bg-white/5 border-white/10 hover:bg-white/10 hover:-translate-y-1 ${getAnimationClass()}`
                } transition-all duration-200 shadow-sm`}
                style={
                  !isCompleted 
                    ? { backgroundColor: theme.buttonColor, color: theme.textColor, borderColor: 'transparent' } 
                    : {}
                }
              >
                <div className="flex items-center gap-4">
                  {getStepIcon(step.type)}
                  <span className="font-medium">{getStepText(step)}</span>
                </div>
                {isCompleted && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                {pendingSteps.includes(index) && <Loader2 className="h-5 w-5 animate-spin text-blue-500" />}
              </button>
              {failedSteps[index] && (
                <p className="text-red-500 text-sm mt-1 px-4">{failedSteps[index]}</p>
              )}
            </div>
            )
          })}
        </div>

        {generalError && (
          <div className="mb-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center justify-center gap-2">
            <AlertCircle className="h-5 w-5 shrink-0" /> {generalError}
          </div>
        )}
        {unlockedUrl ? (
          <a
            href={unlockedUrl}
            className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-sm text-lg font-bold transition-opacity hover:opacity-90 ${getAnimationClass()}`}
            style={{ backgroundColor: theme.buttonColor, color: theme.textColor }}
          >
            Proceed to Link
          </a>
        ) : needsTelegramAuth && allStepsCompleted ? (
          <div className="flex flex-col items-center gap-3">
             <p className="text-sm opacity-80 text-center" style={{ color: theme.textColor }}>
               Please verify your Telegram account to unlock.
             </p>
             <div className="bg-white/5 p-2 rounded-xl border border-white/10">
                <LoginButton 
                  botUsername={process.env.REACT_APP_TELEGRAM_BOT_NAME || "OfficialLinkeyBot"}
                  onAuthCallback={(user) => {
                    const tgData = {
                      id: user.id,
                      user: user
                    };
                    setTelegramData(tgData);
                    localStorage.setItem('telegramAuthData', JSON.stringify(tgData));
                  }} 
                  buttonSize="large" 
                  cornerRadius={12}
                />
             </div>
          </div>
        ) : (
          <button
            onClick={handleUnlock}
            disabled={!allStepsCompleted || verifying}
            className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-sm text-lg font-bold transition-all ${
              allStepsCompleted
                ? `hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] ${getAnimationClass()}`
                : 'cursor-not-allowed opacity-50'
            }`}
            style={{ backgroundColor: theme.buttonColor, color: theme.textColor }}
          >
            {verifying ? 'Verifying...' : 'Unlock Link'}
          </button>
        )}
      </div>
    </div>
  );
};

export default UnlockLink;